import { Component, ElementRef, HostListener, OnInit, Renderer2, ViewEncapsulation, effect } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import versions from 'package.json';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Configuration } from 'src/app/app.constants';
import { Login, UserTokenAuthentication } from 'src/app/core/models/common.model';
import { RelUserRolePermissions } from 'src/app/core/models/role.model';
import { TraxPermission } from 'src/app/core/models/trax-permission.model';
import { User } from 'src/app/core/models/user.model';
import { AuthState } from 'src/app/core/services/auth.service';
import { LoginService } from 'src/app/features/login/components/login.http.service';
import { ServiceProviderService } from 'src/app/features/serviceprovider/components/serviceprovider.http.service';
import { Module, ServiceProviderModule } from 'src/app/features/serviceprovider/serviceprovider.module';
import { AccessGroupPermissionService } from 'src/app/features/usermanagement/accessgrouppermission/components/accessgrouppermission.http.service';
import { UserService } from 'src/app/features/usermanagement/user/components/user.http.service';
import { VenueService } from 'src/app/features/venuemanagement/venue/components/venue.http.service';
import { ClientLogo } from 'src/app/features/venuemanagement/venue/venue.module';
import { setServiceProviderAlertTypes, setUserData } from 'src/app/root-store/user-data/user-action';
import {
  selectIsContextSwitching,
  selectUserData,
  selectVenueDetails,
} from 'src/app/root-store/user-data/user-selector';
import { environment } from 'src/environments/environment';
import { SessionStorageService } from 'ngx-webstorage';
import { Userpilot } from 'userpilot';
import { TRAXIQ_STATE_KEY } from '../../constants/trax-iq-storage.constant';
import { LoaderService } from '../../directives/loader.service';
import { LogoService } from '../../directives/logo.service';
import { PlotlyService } from '../../services/plotly.service';
import { ServiceProviderAlertTypesService } from '../../services/service-provider-alert-types.service';
import { UtilitiesService } from '../utilityservices/utilityservice';

@UntilDestroy()
@Component({
  selector: 'app-navigation',
  templateUrl: 'navigation.component.html',
  styleUrls: ['./navigation.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    UserService,
    ServiceProviderService,
    VenueService,
    LoginService,
    UtilitiesService,
    ServiceProviderAlertTypesService,
  ],
})
export class NavigationComponent implements OnInit {
  TraxPermission = TraxPermission;
  public currentUser: User;

  currentUserVal: User;
  public profileImg = 'assets/blankavatar.png';
  public profileImgSrc: any = null;

  public routeTitle: string;

  public isLocationUserAssignmentShow = false;
  public isShiftStaffScheduleShow = false;
  public isEitherOneOrTheseShow = false;
  public isTouchlessFeedbackShow = false;
  public isTouchlessFeedbackReasonShow = false;
  public isAllowToShareUserShow = false;
  public isDefaultAlertTypeId = false;
  showSensorSettings = false;

  LogoutConfirmationModal = false;
  public modules: Module[] = [];

  public storedLogo: any;
  public logoName = 'assets/interior_logo.png';
  public logoNameSrc: any;
  clientLogo: ClientLogo = new ClientLogo();
  public isSysAdmin = false;

  poweredBy = environment.poweredBy;
  public apiVersion: Observable<string>;
  public appVersion: string = versions.version;
  public defaultlogo = true;
  loggedInUserObj: Login;
  loggedInUser: User;
  public rolePermissions: RelUserRolePermissions[] = [];
  isEmulated = false;
  isReqComplete = false;
  public isEmulation = false;
  public emulatedUserName: string;
  public userName: string;
  public isContextSwitching = false;

  get userPermissions() {
    return this.authState.userPermissions;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private router: Router,
    private authState: AuthState,
    private userservice: UserService,
    public configuration: Configuration,
    public venueService: VenueService,
    private loaderService: LoaderService,
    private logoService: LogoService,
    private serviceProviderService: ServiceProviderService,
    private loginService: LoginService,
    public utilitiesService: UtilitiesService,
    private store: Store,
    private accessGroupPermissionService: AccessGroupPermissionService,
    private plotlyService: PlotlyService,
    private serviceProviderAlertTypesService: ServiceProviderAlertTypesService,
    private sessionSt: SessionStorageService,
  ) {
    this.currentUserVal = this.authState.getCurrentUser();
    this.isSysAdmin = this.currentUserVal.UserApplicationLevel == 'ADMIN';
    if (this.isSysAdmin) {
      this.isAllowToShareUserShow = true;
    }

    // React to app wide logo changes, the effect is torn down with the component.
    effect(() => {
      const logo = this.logoService.currentLogo();
      if (!logo) {
        return;
      }
      this.storedLogo = logo;
      if (logo.ItemType && ['VENUE', 'CORPORATION', 'SERVICE_PROVIDER'].includes(logo.ItemType)) {
        this.logoName = logo.AttachmentURL ?? this.logoName;
        this.defaultlogo = false;
      } else {
        this.defaultlogo = true;
      }
    });
  }

  ngOnInit() {
    this.checkDivVisibility();
    this.updateRouteTitle();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => !event.urlAfterRedirects.startsWith('/landing/')),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.updateRouteTitle();
      });
    this.apiVersion = this.loginService.ApiVersion();
    // getMainLogo() is driven by onUserInfoChanges(), which runs on the initial
    // selectUserData emission and on every subsequent context change
    this.setLoggedInUserInfo(null);
    this.store
      .select(selectUserData)
      .pipe(
        // A single save (e.g. configure default venue) dispatches several user-data updates in sequence, each bumping `lastUpdated` to force an emission. Collapse
        // them into one refresh by reacting only when the user identity or active context actually changes.
        map((user) => this.buildUserRefreshKey(user)),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.onUserInfoChanges();
      });
    this.store
      .select(selectVenueDetails)
      .pipe(untilDestroyed(this))
      .subscribe((venueDetails) => {
        if (!venueDetails) return;
        this.showSensorSettings = venueDetails.ShowSensorSettings;
        this.isTouchlessFeedbackShow = !!venueDetails.EnableTouchlessFeedback;
        this.isTouchlessFeedbackReasonShow = !!venueDetails.EnableTouchlessFeedbackReason;
        if (venueDetails.UserAssignmentRule) {
          this.isLocationUserAssignmentShow = venueDetails.UserAssignmentRule.LocationUserAssignment == 1;
          this.isShiftStaffScheduleShow = venueDetails.UserAssignmentRule.ShiftStaffSchedule == 1;
          this.isEitherOneOrTheseShow = venueDetails.UserAssignmentRule.EitherOneOrThese == 1;
        }
        this.getModulesData();
      });

    this.configuration.CorporationName = this.authState.getStoredCorporationName();
    this.configuration.VenueName = this.authState.getStoredVenueName();
    this.configuration.AlertTypeName = this.authState.getStoredContextAlertTypeName();
    this.store
      .select(selectIsContextSwitching)
      .pipe(untilDestroyed(this))
      .subscribe((isSwitching) => {
        this.isContextSwitching = isSwitching;
      });
    if (this.authState.getStoredIsAllowToShareUser() != null) {
      this.isAllowToShareUserShow = true;
    }
  }

  private updateRouteTitle() {
    let currentRoute = this.router.routerState.root;

    // Login callbacks and landing handoffs can leave the router tree half-built for a moment,
    // so derive the title from the deepest stable route after navigation settles.
    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const firstSegment = currentRoute?.snapshot?.url?.[0]?.path || '';
    this.routeTitle = firstSegment ? firstSegment.split('-')[0] : '';
  }

  refreshData(): void {
    this.checkDivVisibility();
  }

  /**
   * Builds a stable key describing the user identity and active context. Emissions
   * that differ only by the forced `lastUpdated` timestamp map to the same key, so a
   * single save triggers one refresh instead of one per store update - while a genuine
   * profile or context change still produces a new key and refreshes the navigation.
   */
  private buildUserRefreshKey(user: User | null): string {
    return [
      user?.UserId,
      user?.FirstName,
      user?.LastName,
      user?.AttachmentURL,
      user?.ContextRoleId,
      user?.ContextRoleName,
      this.authState.getStoredContextServiceProviderId(),
      this.authState.getStoredContextVenueId(),
      this.authState.getStoredContextAlertTypeId(),
    ].join('|');
  }

  onUserInfoChanges() {
    // The active context (service provider/venue/corporation) can change without a
    // page reload - e.g. when emulating a user - so re-resolve the client logo for
    // the new context instead of leaving the stale one until the next refresh.
    this.getMainLogo();
    this.refreshData();
    this.currentUserVal = this.authState.getCurrentUser();
    this.isSysAdmin = this.currentUserVal.UserApplicationLevel == 'ADMIN';
    if (!this.configuration.isUnauthorizedState && this.currentUserVal?.UserId) {
      const CPuser = {
        UserCPId: this.authState.AESEncryptText(this.currentUserVal.UserId),
      };
      this.userservice.GetById(CPuser).subscribe((data) => {
        this.setLoggedInUserInfo(data.AttachmentURL);
        this.currentUserVal = {
          ...this.currentUserVal,
          FirstName: data.FirstName,
          LastName: data.LastName,
          AttachmentURL: data.AttachmentURL,
          ContextRoleId: data.ContextRoleId,
          ContextRoleName: data.ContextRoleName,
        };
        this.authState.storedTraxCurrentUser(this.currentUserVal);
        this.userName = this.authState.getStoredActualLoggedInUserName();
        if (this.authState.getStoredContextServiceProviderId()) {
          this.getServiceProviderById(this.authState.getStoredContextServiceProviderId());
        }
      });
    }
  }

  setLoggedInUserInfo(AttachmentURL: string) {
    if (AttachmentURL) {
      this.currentUserVal = { ...this.currentUserVal, AttachmentURL };
    }
    this.getProfileImage(this.currentUserVal.AttachmentURL);
  }

  setDefaultPic($event: any) {
    $event.target.src = this.profileImg;
  }

  logout() {
    this.LogoutConfirmationModal = true;
  }

  public signOut() {
    this.userLogout();
  }

  public CloseLogoutConfirmationModal() {
    this.LogoutConfirmationModal = false;
  }

  canAccess(feature: string, access: string) {
    return this.authState.canAccess(feature);
  }

  profiledetails() {
    this.router.navigate(['/profile-menu']);
  }

  navSlide() {
    if (window.matchMedia('(max-width: 767px)').matches) {
      const body = document.getElementsByTagName('body')[0];
      const div = document.getElementById('maincontent');
      body.classList.remove('sidebar-open');
      div.classList.remove('sidebar-open');
    }
  }

  @HostListener('mouseover') onMouseOver() {
    const part = this.el.nativeElement.querySelector('.main-sidebar');
    this.renderer.setStyle(part, 'overflow-y', 'auto');
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e) {
    const ele = document.getElementById('sidebar');
    if (ele != null) {
      const distance = ele.offsetLeft + ele.offsetWidth - e.pageX;
      distance < 5 && distance > -5 ? ele.classList.add('scroll-width') : ele.classList.remove('scroll-width');
    }

    const ele1 = document.getElementById('main-sidebar');
    if (ele1 != null) {
      const distance1 = ele1.offsetLeft + ele1.offsetWidth - e.pageX;
      distance1 < 5 && distance1 > -5 ? ele1.classList.add('scroll-width') : ele1.classList.remove('scroll-width');
    }
  }

  public configurationPopup() {
    this.router.navigate(['/configure-default-venue']);
  }

  public navigationClick(navigationUrl: any) {
    if (
      this.currentUserVal.UserApplicationLevel == 'ADMIN' &&
      (navigationUrl == 'corporation' || navigationUrl == 'venue')
    ) {
      this.router.navigate([navigationUrl]);
    } else {
      const storedVenueId = this.authState.getStoredContextVenueId();

      if (this.authState.isValidContextData()) {
        this.router.navigate([navigationUrl]);
      } else {
        this.router.navigate(['/configure-default-venue']);
      }
    }

    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    } else if (document.getSelection) {
      document.getSelection().empty();
    }
  }

  public getProfileImage(AttachmentURL: string) {
    // const profileimagename = AttachmentURL ? AttachmentURL : this.profileImg;
    this.profileImgSrc = AttachmentURL ? AttachmentURL : this.profileImg;
  }

  public getMainLogo() {
    this.loaderService.display(true);
    this.venueService.GetClientLogo().subscribe(
      (data) => {
        this.clientLogo = data;
        this.authState.storeLogo(this.clientLogo);
        this.logoService.changeLogo(this.clientLogo);

        this.storedLogo = '';
        this.storedLogo = this.authState.getStoredLogo();
        if (this.storedLogo != null) {
          if (this.storedLogo.ItemType == 'VENUE') {
            this.logoName = this.storedLogo.AttachmentURL;
            this.defaultlogo = false;
          } else if (this.storedLogo.ItemType == 'CORPORATION') {
            this.logoName = this.storedLogo.AttachmentURL;
            this.defaultlogo = false;
          } else if (this.storedLogo.ItemType == 'SERVICE_PROVIDER') {
            this.logoName = this.storedLogo.AttachmentURL;
            this.defaultlogo = false;
          } else {
            this.defaultlogo = true;
          }
        }

        this.loaderService.display(false);
      },
      (error) => {
        console.log(
          'venueService GetClientLogo Call Failed. Status:' + error.status + ' Status Text:' + error.statusText,
        );
        this.loaderService.display(false);
      },
    );
  }

  public getServiceProviderById(serviceProviderId: number) {
    this.loaderService.display(true);
    const CPServiceProvider = new ServiceProviderModule();
    CPServiceProvider.ServiceProviderCPId = this.authState.AESEncryptText(serviceProviderId);

    this.serviceProviderService.GetSingle(CPServiceProvider).subscribe(
      (data) => {
        if (data != null) {
          this.serviceProviderAlertTypesService
            .getServiceProviderAlertTypes(serviceProviderId)
            .subscribe((alertTypes) => {
              const activeAlertTypes = (alertTypes ?? []).filter((a) => a.IsActive);
              this.store.dispatch(setServiceProviderAlertTypes({ alertTypes: activeAlertTypes }));
            });
          const defaultAlertTypeId = data.ServiceProviderAlertTypes?.find(
            (a) => a.IsDefault,
          )?.ServiceProviderAlertTypeId;
          const contextAlertTypeId = this.authState.getStoredContextAlertTypeId();
          this.isDefaultAlertTypeId = defaultAlertTypeId === contextAlertTypeId;
          if (data.AllowToShareUser != null) {
            if (data.SPLicenseExpiryDate != null) {
              this.authState.clearSPLicenseExpiryDate();
              this.authState.storedSPLicenseExpiryDate(data.SPLicenseExpiryDate.split('T')[0]);
            }
            if (data.AllowToShareUser) {
              this.isAllowToShareUserShow = true;
            } else {
              this.isAllowToShareUserShow = false;
            }
            this.authState.storedIsAllowToShareUser(data.AllowToShareUser);
          }
        }

        this.loaderService.display(false);
      },
      (error) => {
        console.log(
          'serviceproviderService GetServiceProviderById Call Failed. Status:' +
            error.status +
            ' Status Text:' +
            error.statusText,
        );
        this.loaderService.display(false);
      },
    );
  }

  public getModulesData() {
    this.utilitiesService.isAreaMenuShow = this.utilitiesService.checkVenueFeatureConfiguration('VENUE_TAG_AREA');
  }

  public resizeStatisticsChart() {
    {
      this.loaderService.display(true);
      let svg_container: any;
      const that = this;
      const statisticsChartIds = [
        { chartName: 'locationvisitsbytime' },
        { chartName: 'locationvisitsbyweek' },
        { chartName: 'locationvisitsbymonth' },

        { chartName: 'cleaningalertbylocations' },
        { chartName: 'cleaningalertbymonths' },
        { chartName: 'cleaningalertbyweekdays' },
        { chartName: 'cleaningalertcompleted' },
        { chartName: 'taskcompleted' },
        { chartName: 'quantityofinventoryused' },

        { chartName: 'waterused' },
        { chartName: 'wateruses' },
        { chartName: 'areaWiseScore' },
        { chartName: 'buildingWiseScore' },

        { chartName: 'totalaircraftseats' },

        { chartName: 'inspectionsByLocation' },
        { chartName: 'inspectionsByMonth' },
        { chartName: 'inspectionsByWeekdays' },
        { chartName: 'inspectionsCompleted' },
        { chartName: 'elementsInspected' },

        { chartName: 'positiveNegativeSurveysByWeek' },
        { chartName: 'positiveSurveyByTime' },
        { chartName: 'negativeSurveyByTime' },
        { chartName: 'satisfactionSummaryChart' },
        { chartName: 'positiveSurveyByTime' },
        { chartName: 'negativeSurveyByTime' },
        { chartName: 'totalSurveysChart' },
        { chartName: 'negativeSurveyBreakdown' },

        // { 'chartName': '24Plot' },
        // { 'chartName': 'dayPlot' },
        // { 'chartName': 'weekendPlot' },
        // { 'chartName': 'cumulativePlot' },
        { chartName: 'throughputSurveyCount' },
        { chartName: 'throughputSurveyCountLineChart' },

        { chartName: 'inspectionperformed' },
        { chartName: 'inspectionscore' },
        { chartName: 'ticketscreated' },
      ];

      setTimeout(async () => {
        for (let chart = 0; chart < statisticsChartIds.length; chart++) {
          const getChart = document.getElementById(statisticsChartIds[chart].chartName);
          if (getChart) {
            await this.plotlyService.resize(getChart);
            const window_height = window.innerHeight;
            const content_div_height = document.getElementById('chart-container').offsetHeight;
            if (content_div_height > window_height - 1) {
              svg_container = document
                .getElementById('chart-container')
                .getElementsByClassName('plot-container')[0]
                .getElementsByClassName('svg-container')[0];
              svg_container.style.height = window_height - 1 + 'px';
              await this.plotlyService.resize(getChart);
            }
          }
        }
        that.loaderService.display(false);
      }, 250);
    }
  }

  public userLogout() {
    this.loaderService.display(true);
    const loggedInUser = this.authState.getStoredActualLoggedInUserEmail();
    const emulatedUser = this.authState.getEmulatedUserEmail();

    const userObj: Login = {
      ip_address: this.authState.getStoredIPAddress(),
      user_id: this.currentUserVal.UserId,
    };

    this.loginService.UserLogout(userObj).subscribe(
      (_data) => {
        const isTraxApp = this.configuration.projectTitle.toLowerCase() === 'trax';
        const email = loggedInUser;
        if (this.isEmulation) {
          this.loginService.LogLogout(loggedInUser, emulatedUser, false).subscribe(() => {
            this.authState.logout();
          });
        } else {
          this.authState.logout();
        }
        this.loaderService.display(false);
        this.router.navigate(['/login']);
        this.loginService.SessionLogout(email, isTraxApp).subscribe(
          (data) => {
            if (data.endSessionUrl !== '') {
              this.loginService.redirectUser(data);
            }
          },
          () => {},
        );
      },
      (error) => {
        console.log('loginService UserLogout Call Failed. Status:' + error.status + ' Status Text:' + error.statusText);
        this.loaderService.display(false);
        this.authState.logout();
        this.router.navigate(['/login']);
      },
    );
  }

  checkDivVisibility(): void {
    const storedValue = this.authState.getIsEmulation();
    this.isEmulation = storedValue;
    if (this.isEmulation) {
      this.emulatedUserName = this.authState.getEmulatedUser();
    }
  }

  isUserEmulated(): boolean {
    const storedValue = this.authState.getIsEmulation();
    this.isEmulation = storedValue;
    return this.isEmulation;
  }

  public stopEmulation() {
    const emulatedUser = this.authState.getEmulatedUserEmail();
    this.utilitiesService.openConfirmModal('Do you want to end the emulation?', (result: boolean) => {
      if (result) {
        this.loaderService.display(true);
        const loggedInUser = this.authState.getStoredActualLoggedInUserEmail();
        this.isEmulation = false;
        this.authState.storeIsEmulation(false);
        // Ending emulation switches context back to the actual user, so the persisted Trax IQ
        // filter/slicer/tab view is now stale — clear it. See features/trax-iq.
        this.sessionSt.clear(TRAXIQ_STATE_KEY);
        this.loginService.EmulateUser(loggedInUser, emulatedUser, false).subscribe(
          (data) => {
            this.loggedInUser = data;
            this.isReqComplete = false;
            this.loaderService.display(false);
            this.authState.clearTraxCurrentUser();
            this.authState.storedTraxCurrentUser(this.loggedInUser);
            if (this.loggedInUser.DefaultVenue) {
              this.setServiceProvider();
              this.setCorporation();
              this.setVenue();
            } else {
              this.configuration.contextServiceProviderId = 0;
              this.configuration.contextCorporationId = 0;
              this.configuration.contextVenueId = 0;
            }
            this.configuration.contextDefaultAlertTypeId = this.loggedInUser.DefaultAlertTypeId ?? 0;

            this.authState.setServiceProviderAlertTypeDetails(
              this.loggedInUser.DefaultAlertType,
              this.loggedInUser.DefaultAlertTypeId,
            );

            this.insertUserTokenAuthentication();

            // User Pilot for user training
            Userpilot.identify(data.Email, {
              name: data.FirstName + ' ' + data.LastName,
              email: data.Email,
              created_at: data.AddedOn,
              company: !!data.ServiceProvider // optional
                ? {
                    id: data.ServiceProvider.Ids, // Company Unique ID
                    created_at: data.ServiceProvider.AddedOn, // ISO8601 Date
                  }
                : null,
            });
          },
          (error) => {
            this.isReqComplete = false;
            this.loaderService.display(false);
            console.log('loginService login Call Failed. Status:' + error.status + ' Status Text:' + error.statusText);
          },
        );
      }
    });
  }

  /**
   * insertUserTokenAuthentication method
   */
  public insertUserTokenAuthentication() {
    this.loaderService.display(true);
    const payload: Partial<UserTokenAuthentication> = {
      ServiceProviderId: this.configuration.contextServiceProviderId,
      CorporationId: this.configuration.contextCorporationId,
      Venue: {
        VenueId: this.configuration.contextVenueId,
      },
      ServiceProviderAlertTypeId: this.configuration.contextAlertTypeId,
    };

    this.loginService.InsertUserTokenAuthentication(payload).subscribe(
      () => {
        this.loaderService.display(false);
        this.setVenueDetailsAndPermissions();
      },
      (error) => {
        console.log(
          'loginService InsertUserTokenAuthentication Call Failed. Status:' +
            error.status +
            ' Status Text:' +
            error.statusText,
        );
        this.loaderService.display(false);
        this.setVenueDetailsAndPermissions();
      },
    );
  }

  /**
   * Method to fetch user permissions and set default navigation
   */
  private setRolePermissions() {
    this.loaderService.display(true);
    const searchfilter = { SearchQuery: '', GenericSearch: 0 };

    this.accessGroupPermissionService.GetAllPermissions(JSON.stringify(searchfilter), -1, -1).subscribe(
      (data) => {
        this.rolePermissions = data;
        this.loaderService.display(false);
        this.store.dispatch(setUserData({ user: this.loggedInUser }));
        this.authState.setUserRolePermissions(this.rolePermissions);
        this.authState.setDefaultNavigation(this.loggedInUser, this.rolePermissions, null);
      },
      (error) => {
        this.loaderService.display(false);
        if (error.status == 401) {
          this.authState.logout();
        }
      },
    );
  }

  setVenueDetailsAndPermissions() {
    if (this.authState.getStoredContextVenueId()) {
      this.authState.getVenueById(this.authState.getStoredContextVenueId());
      this.setRolePermissions();
    } else {
      this.setRolePermissions();
    }
  }

  setServiceProvider() {
    if (this.loggedInUser.DefaultVenue.ServiceProvider) {
      this.configuration.contextServiceProviderId = this.loggedInUser.DefaultVenue.ServiceProvider.ServiceProviderId;
      this.authState.storedContextServiceProviderId(this.configuration.contextServiceProviderId);

      this.configuration.ServiceProviderName = this.loggedInUser.DefaultVenue.ServiceProvider.ServiceProviderName;
      this.authState.storedServiceProviderName(this.configuration.ServiceProviderName);
    }
  }

  /**
   * Method to set the corporation details
   */
  setCorporation() {
    if (this.loggedInUser.DefaultVenue.Corporation && this.loggedInUser.DefaultVenue.Corporation.CorporationId > 0) {
      this.configuration.contextCorporationId = this.loggedInUser.DefaultVenue.Corporation.CorporationId;
      this.authState.storedContextCorporationId(this.configuration.contextCorporationId);

      this.configuration.CorporationName = this.loggedInUser.DefaultVenue.Corporation.CorporationName;
      this.authState.storedCorporationName(this.configuration.CorporationName);
    } else {
      this.configuration.CorporationName = null;
    }
  }

  /**
   * Method to set the venue details
   */
  setVenue() {
    if (this.loggedInUser.DefaultVenue.VenueId > 0) {
      this.configuration.contextVenueId = this.loggedInUser.DefaultVenue.VenueId;
      this.authState.storedContextVenueId(this.configuration.contextVenueId);

      this.configuration.VenueName = this.loggedInUser.DefaultVenue.VenueName;
      this.authState.storedVenueName(this.configuration.VenueName);
    } else {
      this.configuration.contextVenueId = 0;
      this.configuration.VenueName = null;
    }
  }
}
